'use client';
import * as React from 'react';
import { cn } from '@/lib/utils';

function TooltipProvider({ children }: { children: React.ReactNode; delayDuration?: number }) {
  return <>{children}</>;
}

function Tooltip({
  children,
  delayDuration,
  style,
  className,
  ...rest
}: {
  children: React.ReactNode;
  delayDuration?: number;
  style?: React.CSSProperties;
  className?: string;
}) {
  return (
    <span className={cn('group/tooltip relative inline-grid', className)} style={style}>
      {children}
    </span>
  );
}

function TooltipTrigger({ children, asChild }: { children: React.ReactNode; asChild?: boolean }) {
  return <>{children}</>;
}

function getPositionClasses(side: string, align?: string) {
  const isVertical = side === 'top' || side === 'bottom';
  if (isVertical) {
    const main = side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2';
    const cross = align === 'start' ? 'left-0' : align === 'end' ? 'right-0' : 'left-1/2 -translate-x-1/2';
    return `${main} ${cross}`;
  }
  const main = side === 'left' ? 'right-full mr-2' : 'left-full ml-2';
  const cross = align === 'start' ? 'top-0' : align === 'end' ? 'bottom-0' : 'top-1/2 -translate-y-1/2';
  return `${main} ${cross}`;
}

function TooltipContent({
  children,
  className,
  side = 'top',
  align,
  sideOffset,
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  [key: string]: unknown;
}) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const shiftRef = React.useRef({ x: 0, y: 0 });
  const sideRef = React.useRef(side);
  React.useEffect(() => {
    sideRef.current = side;
  }, [side]);
  const pointerXRef = React.useRef<number | null>(null);
  const [shift, setShift] = React.useState({ x: 0, y: 0 });
  const [cappedWidth, setCappedWidth] = React.useState<number | null>(null);
  const [cappedHeight, setCappedHeight] = React.useState<number | null>(null);
  // True wanneer een clippende voorouder (bv. een balk-rail met overflow-hidden) aan geen
  // van beide kanten van de trigger ruimte laat: de tooltip kan daarbinnen nooit zichtbaar
  // worden en ontsnapt dan via position: fixed.
  const [escaped, setEscaped] = React.useState(false);

  // The tooltip is positioned purely with CSS relative to its trigger, so a trigger near
  // the window edge pushes it off-screen where it gets clipped. Nudge it back in-view on
  // hover/focus.
  React.useEffect(() => {
    const node = ref.current;
    const group = node?.parentElement;
    if (!node || !group) return;

    const reposition = () => {
      const padding = 8;
      // Ancestors with overflow other than visible clip the tooltip too (e.g. the
      // scrollable main pane), so clamp against the intersection of the viewport
      // and every clipping ancestor's box.
      let minLeft = 0;
      let maxRight = window.innerWidth;
      let minTop = 0;
      let maxBottom = window.innerHeight;
      const groupRect = group.getBoundingClientRect();
      const vertical = sideRef.current === 'top' || sideRef.current === 'bottom';
      const gap = 8;
      // scrollWidth/Height: de ongekapte maat, ook als een eerdere meting hem heeft ingeperkt.
      const needed =
        (vertical
          ? node.scrollHeight + node.offsetHeight - node.clientHeight
          : node.scrollWidth + node.offsetWidth - node.clientWidth) +
        gap +
        padding;
      let escape = false;
      for (let el = group.parentElement; el; el = el.parentElement) {
        const { overflowX, overflowY } = getComputedStyle(el);
        if (overflowX !== 'visible' || overflowY !== 'visible') {
          const r = el.getBoundingClientRect();
          const before = vertical ? groupRect.top - r.top : groupRect.left - r.left;
          const after = vertical ? r.bottom - groupRect.bottom : r.right - groupRect.right;
          if (before < needed && after < needed) {
            escape = true;
            break;
          }
          minLeft = Math.max(minLeft, r.left);
          maxRight = Math.min(maxRight, r.right);
          minTop = Math.max(minTop, r.top);
          maxBottom = Math.min(maxBottom, r.bottom);
        }
      }
      if (escape) {
        // position: fixed wordt door geen enkele overflow-voorouder geclipt.
        minLeft = 0;
        maxRight = window.innerWidth;
        minTop = 0;
        maxBottom = window.innerHeight;
      }
      setEscaped(escape);
      const availableW = maxRight - minLeft - padding * 2;
      const availableH = maxBottom - minTop - padding * 2;
      const rect = node.getBoundingClientRect();
      // Narrower/shorter than the clip area is a precondition for fitting it back in view.
      setCappedWidth(rect.width > availableW ? availableW : null);
      setCappedHeight(rect.height > availableH ? availableH : null);
      const left = rect.left - shiftRef.current.x;
      const right = rect.right - shiftRef.current.x;
      const top = rect.top - shiftRef.current.y;
      const bottom = rect.bottom - shiftRef.current.y;
      let nextX = 0;
      let nextY = 0;
      if (escape) {
        // Ontsnapt: de CSS-positionering t.o.v. de trigger geldt niet meer, dus reken de
        // gewenste plek zelf uit en schuif er vanaf de gemeten positie naartoe.
        const side = sideRef.current;
        let x: number;
        let y: number;
        if (vertical) {
          const anchorX =
            pointerXRef.current != null && groupRect.width > rect.width
              ? pointerXRef.current
              : groupRect.left + groupRect.width / 2;
          x = anchorX - rect.width / 2;
          const above = groupRect.top - gap - rect.height;
          const below = groupRect.bottom + gap;
          const fitsAbove = above >= padding;
          const fitsBelow = below + rect.height <= maxBottom - padding;
          y = side === 'top' ? (fitsAbove || !fitsBelow ? above : below) : fitsBelow || !fitsAbove ? below : above;
        } else {
          y = groupRect.top + groupRect.height / 2 - rect.height / 2;
          const before = groupRect.left - gap - rect.width;
          const after = groupRect.right + gap;
          const fitsBefore = before >= padding;
          const fitsAfter = after + rect.width <= maxRight - padding;
          x = side === 'left' ? (fitsBefore || !fitsAfter ? before : after) : fitsAfter || !fitsBefore ? after : before;
        }
        x = Math.max(padding, Math.min(x, maxRight - padding - rect.width));
        y = Math.max(padding, Math.min(y, maxBottom - padding - rect.height));
        nextX = x - left;
        nextY = y - top;
        if (nextX !== shiftRef.current.x || nextY !== shiftRef.current.y) {
          shiftRef.current = { x: nextX, y: nextY };
          setShift({ x: nextX, y: nextY });
        }
        return;
      }
      // Trigger breder dan de tooltip (lange boekingsbalk): het midden van de trigger ligt
      // dan ver van de muis of zelfs buiten beeld. Veranker op de muispositie.
      if (vertical && pointerXRef.current != null && groupRect.width > rect.width) {
        nextX = pointerXRef.current - (left + right) / 2;
      }
      // Past de tooltip niet aan de gevraagde kant, klap dan om naar de overkant in
      // plaats van hem over de trigger heen te schuiven.
      if (vertical) {
        const fitsAbove = groupRect.top - gap - rect.height >= minTop + padding;
        const fitsBelow = groupRect.bottom + gap + rect.height <= maxBottom - padding;
        if (sideRef.current === 'top' && top < minTop + padding && fitsBelow) {
          nextY = groupRect.bottom + gap - top;
        } else if (sideRef.current === 'bottom' && bottom > maxBottom - padding && fitsAbove) {
          nextY = groupRect.top - gap - rect.height - top;
        }
      }
      if (left + nextX < minLeft + padding) nextX = minLeft + padding - left;
      else if (right + nextX > maxRight - padding) nextX = maxRight - padding - right;
      if (top + nextY < minTop + padding) nextY = minTop + padding - top;
      else if (bottom + nextY > maxBottom - padding) nextY = maxBottom - padding - bottom;
      if (nextX !== shiftRef.current.x || nextY !== shiftRef.current.y) {
        shiftRef.current = { x: nextX, y: nextY };
        setShift({ x: nextX, y: nextY });
      }
    };

    // Meet direct én in het volgende frame: de eerste meting kan plaatsvinden vóór de
    // layout rond is (fonts/reflow), waardoor de correctie anders één hover achterloopt.
    const repositionTwice = () => {
      reposition();
      requestAnimationFrame(reposition);
    };

    const onPointerEnter = (e: PointerEvent) => {
      pointerXRef.current = e.clientX;
      repositionTwice();
    };
    // Volg de muis alleen over brede triggers; per frame hooguit één meting.
    let moveFrame = 0;
    const onPointerMove = (e: PointerEvent) => {
      pointerXRef.current = e.clientX;
      if (moveFrame || group.getBoundingClientRect().width <= node.getBoundingClientRect().width) return;
      moveFrame = requestAnimationFrame(() => {
        moveFrame = 0;
        reposition();
      });
    };

    repositionTwice();
    group.addEventListener('pointerenter', onPointerEnter);
    group.addEventListener('pointermove', onPointerMove);
    group.addEventListener('focusin', repositionTwice);
    window.addEventListener('resize', reposition);
    return () => {
      group.removeEventListener('pointerenter', onPointerEnter);
      group.removeEventListener('pointermove', onPointerMove);
      if (moveFrame) cancelAnimationFrame(moveFrame);
      group.removeEventListener('focusin', repositionTwice);
      window.removeEventListener('resize', reposition);
    };
  }, []);

  return (
    <span
      ref={ref}
      role="tooltip"
      translate="no"
      style={{
        // standalone `translate`, so it composes with (instead of overriding) the
        // -translate-x-1/2 centering that Tailwind emits as `transform`
        ...(escaped ? { position: 'fixed' as const, inset: 'auto' } : null),
        ...(shift.x || shift.y ? { translate: `${shift.x}px ${shift.y}px` } : null),
        ...(cappedWidth != null ? { maxWidth: `${cappedWidth}px` } : null),
        ...(cappedHeight != null ? { maxHeight: `${cappedHeight}px` } : null),
      }}
      className={cn(
        'absolute z-50 pointer-events-none',
        // w-max: absolute positioning shrink-wraps to the trigger's width (the containing block),
        // so tooltips on narrow labels rendered as tall single-word columns without it.
        'w-max max-w-sm',
        'overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md',
        'opacity-0 transition-opacity duration-150',
        'group-hover/tooltip:opacity-100 group-focus-within/tooltip:opacity-100',
        getPositionClasses(side, align),
        className,
      )}
    >
      {children}
    </span>
  );
}

TooltipContent.displayName = 'TooltipContent';

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
